import { Component, OnInit, Inject, ViewChild } from "@angular/core";
import { Observable, of } from "rxjs";
import { Router, ActivatedRoute, ParamMap } from "@angular/router";
import {
  MatDialog as MatDialog,
  MatDialogRef as MatDialogRef,
  MatDialogConfig as MatDialogConfig,
  MAT_DIALOG_DATA as MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { AlertifyService } from "../../_models/alertify.service";

import { TemplateService } from "../service/template.service";
import {
  AppSect,
  TemplateContent,
  ApplicabilityReviewDialogData,
} from "../model/TemplateRulebook";
import { diffString, decodeHtml, stripHtml } from "../shared/text-diff.util";
import { AnswerQuestionComponent } from "./answer-question/answer-question.component";
import { ListRulebookComponent } from "./list-rulebook/list-rulebook.component";

@Component({
  selector: "app-edit-applicability",
  templateUrl: "./edit-applicability.component.html",
  styleUrls: ["./edit-applicability.component.css"],
  standalone: false,
})
export class EditApplicabilityComponent implements OnInit {
  csskey: string;
  auditName: string;
  appkey: string;
  appSect$: Observable<AppSect[]>;
  selectedRbName: string;
  selectedAppkey: number;
  rulebookList: TemplateContent[];
  currentTopic: string;
  isAnswering: boolean;
  sharePermission: boolean; //
  showSecondLevelScreening = false;

  @ViewChild(AnswerQuestionComponent) answerQuestion: AnswerQuestionComponent;
  // Backs the compact Previous/Next buttons shown above app-answer-question below
  // 920px, once app-list-rulebook (and its own Previous/Next) is hidden - delegates to
  // the same component rather than duplicating its navigation logic.
  @ViewChild(ListRulebookComponent) listRulebook: ListRulebookComponent;

  // Tracks in-flight updateAppAnswer() saves so onBackSummary() can wait for them to land
  // before navigating - otherwise the summary page's getContent() can race ahead of the
  // last answer's save (which is what clears 'Needs Review' server-side) and read stale data.
  private pendingSaves: Promise<void>[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertifyService,
    private dialog: MatDialog,
    public service: TemplateService
  ) {}

  ngOnInit() {
    this.selectedRbName =
      this.service.decodeHtml(this.service.getRbName().value ?? "") ?? "";
    this.isAnswering = false;
    this.service.getCurrent().subscribe((res) => {
      //let item = res.findIndex(x => x.acronym == "Custom"); // Do not display custom question on applicablity page.

      this.rulebookList = res;
    });

    this.auditName = this.service.getAuditName();

    this.route.paramMap.subscribe(
      (paraMap) => (
        (this.csskey = paraMap.get("id")),
        (this.appkey = paraMap.get("appkey")),
        (this.sharePermission = this.service.isTemplateEditable(+this.csskey))
      )
    );
    this.currentTopic = this.service.getTopic(parseInt(this.appkey));
    this.loadApplicability(this.csskey, this.appkey);

    this.service.getShowSecondLevelScreening(+this.csskey).subscribe(
      (res) => (this.showSecondLevelScreening = res),
      (err) => {
        console.error("getShowSecondLevelScreening() failed: " + err.error);
      }
    );
  }

  /**
   * Toggle whether the second-level (paragraph) applicability screening checklist is shown
   * for this template. Turning it off while any paragraph anywhere is still excluded doesn't
   * just fail - it offers to restore (re-include) every excluded paragraph as part of turning
   * it off, via a Cancel/Confirm dialog. Only Confirm actually clears anything; the toggle
   * stays on until then.
   *
   * Whether any paragraph is excluded is checked client-side first, against whatever
   * paragraph checklists are currently rendered (the rulebook the user is on right now) - a
   * fast pre-check, not the authoritative one, since exclusions in other rulebooks the user
   * hasn't opened aren't visible to the frontend. Failing that, the backend's own template-
   * wide check (409) is the fallback that catches those.
   */
  onToggleSecondLevelScreening(show: boolean) {
    if (!show && this.answerQuestion?.hasExcludedParagraphs()) {
      this.confirmClearExclusionsThenTurnOff();
      return;
    }

    this.applyShowSecondLevelScreening(show, false);
  }

  private applyShowSecondLevelScreening(show: boolean, clearExclusions: boolean) {
    this.service
      .setShowSecondLevelScreening(+this.csskey, show, clearExclusions)
      .subscribe(
        () => {
          this.showSecondLevelScreening = show;
          // Only the confirmed clear-and-restore actually changes any data beyond the
          // toggle itself, so that's the only case worth a success message - a plain
          // on/off flip doesn't need one, same as before.
          if (clearExclusions) {
            this.alertService.success(
              "All excluded paragraphs have been restored."
            );
          }
        },
        (err) => {
          if (!show && err.status === 409 && !clearExclusions) {
            this.confirmClearExclusionsThenTurnOff();
            return;
          }
          if (clearExclusions) {
            this.alertService.error(
              err.error || "Failed to restore the excluded paragraphs."
            );
          } else {
            this.alertService.confirmOk(
              err.error ||
                "Please clear all Detailed Requirement Screening exclusions before turning this off."
            );
          }
          this.revertToggleVisual();
        }
      );
  }

  private confirmClearExclusionsThenTurnOff() {
    this.alertService.confirmOkCancel(
      "STP AuditHub",
      "Turning off Detailed Requirement Screening will restore (re-include) all excluded paragraphs in this template. Continue?",
      () => this.applyShowSecondLevelScreening(false, true),
      () => this.revertToggleVisual()
    );
  }

  /**
   * MatSlideToggle flips its own internal `checked` state on click regardless of our
   * [checked] binding, and Angular's change detection only re-pushes an @Input when the
   * bound expression's value actually changed since the last check. When we reject a click
   * without ever changing showSecondLevelScreening, its value never differs, so Angular
   * never re-syncs the switch - it's left showing whatever the click already flipped it to,
   * even though nothing was saved. Force a real value change (flip away, then back on the
   * next tick) so the switch gets pushed back to the true value.
   */
  private revertToggleVisual() {
    const current = this.showSecondLevelScreening;
    this.showSecondLevelScreening = !current;
    setTimeout(() => (this.showSecondLevelScreening = current));
  }

  loadApplicability(csskey: string, appkey: string) {
    try {
      this.service.showLoadingAnimation(true);
      this.appSect$ = new Observable<AppSect[]>();
      this.appSect$ = this.service.getApplicability(csskey, appkey);
      let re = /<b>Question:<\/b>/gi;

      this.appSect$.subscribe(
        (res) => {
          this.service.showLoadingAnimation(false);
          res.forEach((element) => {
            element.question = this.service
              .decodeHtml(element.question)
              .replace(re, ""); //decode html entities; remove <b>Question:</b> tag.
            element.exemption = this.service.decodeHtml(element.exemption);
          });
          // Only clear 'Needs Review' if every significant-change flag on this
          // rulebook's questions/exemptions has already been answered - merely
          // opening the page should not clear it.
          if (res.every((a) => !a.sigChangeUpdate)) {
            this.service.revokeReviewFlag(+csskey, +appkey, 1);
          }
        },
        (err) => {
          console.error("got an error:" + err);
          this.service.showLoadingAnimation(false);
          this.alertService.error(err.message);
        },
        () => this.service.showLoadingAnimation(false)
      );
    } catch (Error) {
      this.alertService.error(Error.message);
    }
  }

  onClickRulebook(appkey: number) {
    //console.log("received click event from child: " + appkey);
    this.selectedRbName = this.service.getRbName().value;
    this.appkey = this.service.getKey().value.toString();
    this.currentTopic = this.service.getTopic(parseInt(this.appkey));

    this.loadApplicability(this.csskey, appkey.toString());
  }

  onBackSummary() {
    // Wait for every in-flight answer save first - each one's success handler re-caches
    // this component's own (possibly stale, e.g. needsReview) rulebookList snapshot, so
    // clearing the cache has to happen after all of those, not before, or it just gets
    // immediately overwritten again.
    Promise.all(this.pendingSaves).then(() => {
      this.service.removeTemplateLocalStorage(+this.csskey);
      var myurl = "audithub/apptool/edit/" + this.csskey;
      this.router.navigateByUrl(myurl).then((err) => {
        console.log(err);
      });
    });
  }

  /**
   * Get the mirrored appkey of the rulebook.
   * @param appkey: the appkey of the rulebook.
   * @return: the mirrored appkey if found, otherwise return -1.
   */
  getMirroredAppkey(appkey: number): number {
    if (this.service.isDualLanguageTemplate() === false) return -1;
    for (const acronym of this.rulebookList) {
      for (const topic of acronym.topics) {
        const rb = topic.rulebooks.find((rb) => rb.appKey === appkey);
        if (rb) {
          const mirroredAcronym = this.service.getDualLanguageAcronym(
            acronym.acronym
          );
          if (!mirroredAcronym) return -1;
          const mirroredPub = this.rulebookList.find(
            (a) =>
              a.acronym === mirroredAcronym &&
              a.releaseNum === acronym.releaseNum
          );
          if (!mirroredPub) return -1;
          const mirroredTopic = mirroredPub.topics.find(
            (t) => t.topic === topic.topic
          );
          if (!mirroredTopic || !mirroredTopic.rulebooks.length) return -1;
          return mirroredTopic.rulebooks[0].appKey;
        }
      }
    }
    return -1;
  }

  /**
   * Answer the applicability questions.
   * @param answer
   */
  onAnswerApp(answer: string) {
    this.updateAppAnser(
      this.csskey,
      this.appkey ? parseInt(this.appkey) : 0,
      answer
    );
    let mirroredAppkey = this.getMirroredAppkey(
      this.appkey ? parseInt(this.appkey) : 0
    );
    if (mirroredAppkey !== -1) {
      this.updateAppAnser(this.csskey, mirroredAppkey, answer);
    }
  }

  updateAppAnser(csskey: string, appkey: number, answer: string) {
    this.service.showLoadingAnimation(true);
    this.isAnswering = true;
    const save = new Promise<void>((resolve) => {
      this.service.updateAppAnswer(csskey, appkey, answer).subscribe(
        (val) => {
          this.isAnswering = false;
          this.selectedRbName = this.selectedRbName + "";
          const rb = this.rulebookList
            .flatMap((arcon) => arcon.topics)
            .flatMap((tp) => tp.rulebooks)
            .find((rb) => rb.appKey == val.appkey);

          if (rb) {
            rb.percentage = this.service.calRulebookPercentage(answer);
            rb.checkNa = this.service.calCheckNa(answer);
            rb.appAnswer = answer;
          }
          // Update the cache
          this.service.cacheRulebookLocalStorage(
            parseInt(this.csskey),
            this.rulebookList
          );
          resolve();
        },
        (error) => {
          console.error("updateAppAnser() return: " + error.error);
          this.isAnswering = false;
          resolve();
        },
        () => {
          this.service.showLoadingAnimation(false);
        }
      );
    });
    this.pendingSaves.push(save);
  }

  /**
   * Called whenever an applicability question/exemption is answered. Once none of the
   * rulebook's questions are still flagged with a significant change, patch its 'Needs
   * Review' status in the cache immediately. This is only a best-effort local patch -
   * updateAppAnser's own async re-cache of its in-memory rulebookList (needsReview never
   * updated there) can still overwrite it, which is why onBackSummary() drops the whole
   * cache after all saves land, forcing a real re-fetch instead of relying on this alone.
   */
  onSigResolved(resolved: boolean) {
    if (resolved) {
      this.service.revokeReviewFlag(+this.csskey, +this.appkey, 1);
    }
  }

  /**
   * Show what changed for an applicability question flagged with a significant update.
   */
  onViewSigChange(ac: AppSect) {
    this.service.showLoadingAnimation(true);
    this.service
      .reviewApplicabilityUpdate(+this.csskey, +this.appkey, ac.appSectKey)
      .subscribe(
        (content) => {
          this.service.showLoadingAnimation(false);
          const dialogConfig = new MatDialogConfig();
          dialogConfig.data = {
            content: content,
            rbName: this.selectedRbName,
            rbSectNum: ac.rbSectNum,
            csskey: +this.csskey,
            appkey: +this.appkey,
          } as ApplicabilityReviewDialogData;
          dialogConfig.width = "800px";
          dialogConfig.position = { top: "5vh" };
          this.dialog.open(DisplayApplicabilityReviewDialog, dialogConfig);
        },
        (err) => {
          this.service.showLoadingAnimation(false);
          console.error(err);
          this.alertService.error(err.error);
        }
      );
  }
}

/**
 * Dialog window to display the old/new question diff for a single applicability
 * question that was flagged with a significant change.
 */
@Component({
  selector: "displayApplicabilityReview",
  templateUrl: "applicability-review.html",
  styleUrls: ["./edit-applicability.component.css"],
  standalone: false,
})
export class DisplayApplicabilityReviewDialog implements OnInit {
  newQuestion: string;
  dialogTitle = "Applicability Question Update Review";
  sectionTitle: string;
  // No old counterpart means this question was just added (a new rulebook, or a new
  // section within an existing one) - there was never a previous answer to drop.
  isNewQuestion: boolean;

  constructor(
    public dialogRef: MatDialogRef<DisplayApplicabilityReviewDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ApplicabilityReviewDialogData
  ) {}

  ngOnInit() {
    // RbSectNum 0 is the rulebook-level question; anything above is a numbered section.
    this.sectionTitle =
      this.data.rbSectNum > 0
        ? `${this.data.rbName} - Section ${this.data.rbSectNum}`
        : this.data.rbName;

    this.isNewQuestion = !this.data.content.oldQuestion;
    // TblAppSection.Question already carries an embedded '<b>Question:</b>' label, HTML-
    // encoded as stored - decode it first, then strip the tag, same order and pattern
    // loadApplicability() uses for the question list, since the dialog template supplies
    // its own 'Question:' label.
    const stripLabel = (html: string) =>
      decodeHtml(html ?? "").replace(/<b>Question:<\/b>/gi, "");
    this.newQuestion = diffString(
      stripHtml(stripLabel(this.data.content.oldQuestion)),
      stripHtml(stripLabel(this.data.content.newQuestion))
    );
  }

  onClose(): void {
    this.dialogRef.close(null);
  }
}
